/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import {
    Button,
    Icon,
    FlexRow,
    FlexCol,
    VisualOnlyLabel,
    NumberField,
} from 'sarsaparilla';

import {
    NoQuotaMods,
    hasRule,
    issuanceStatuses,
    getValidationErrorStyle,
} from '../../utils/utilsNew';
import {
    REDUCIBLE_GROUP_SIZE_KEY_NAME,
    PERMIT_COMPLETE,
    IS_GROUP_MEMBER_EMPTY,
    OVERRIDE_MAX_VALUE,
    ENCHANTMENT_ID,
} from '../../constants';
import * as PROPS from '../../props/permit';

export class SimpleGroupMemberCounter extends React.Component {
    static propTypes = {
        guestTypes: PropTypes.arrayOf(PropTypes.shape({})),
        groupMembers: PropTypes.arrayOf(PropTypes.shape({})),
        minMemberQuota: PropTypes.number,
        maxMemberQuota: PropTypes.number,
        onChange: PropTypes.func.isRequired,
        isBypassRulesValidations: PropTypes.bool,
        entryDate: PropTypes.instanceOf(Gnoment),
        isQuotaModifyAllowed: PropTypes.bool,
        issuance: PROPS.ISSUANCE,
        permit: PROPS.PERMIT,
        validation: PropTypes.shape({}),
        onModifyDetails: PropTypes.func,
        hasSecureQuotaText: PropTypes.bool,
        modifyDetailsButtonText: PropTypes.string,
    };

    static defaultProps = {
        guestTypes: [
            {
                key: 'adult',
                type: 'Adult',
                description: '13 years or older',
            },
            {
                key: 'child',
                type: 'Child',
                description: '12 years or younger',
            },
        ],
        groupMembers: [],
        minMemberQuota: 1,
        maxMemberQuota: 100,
    };

    componentDidMount() {
        const members = this.props.groupMembers.filter(
            (member) => member.isAdult || member.isChild
        );
        const minRequired = Math.max(this.props.minMemberQuota, members.length);

        if (minRequired && minRequired > members.length) {
            this.handleGuestChange(minRequired, 'adult');
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.minMemberQuota &&
            this.props.minMemberQuota !== prevProps.minMemberQuota &&
            this.props.groupMembers?.length < this.props.minMemberQuota
        ) {
            this.handleGuestChange(this.props.minMemberQuota, 'adult');
        }
    }

    onChangeDetailsClick = () => {
        this.props.onModifyDetails(this.props.issuance);
    };

    getGroupText() {
        const maxGroupSize = this.props.maxMemberQuota;

        if (this.props.hasSecureQuotaText) {
            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be increased if there are enough spots available
                    for the day
                </p>
            );
        }

        if (
            !maxGroupSize ||
            maxGroupSize >= SimpleGroupMemberCounter.defaultProps.maxMemberQuota
        ) {
            return (
                <p>
                    Add everyone that will be going with you. This can be changed any time
                    before the permit is issued.
                </p>
            );
        }

        if (hasRule(REDUCIBLE_GROUP_SIZE_KEY_NAME, this.props.permit.rules, '')) {
            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be decreased.
                </p>
            );
        }

        if (this.props.permit.id === ENCHANTMENT_ID) {
            return (
                <p>
                    Add everyone that will be going with you. Maximum of {maxGroupSize}{' '}
                    members at this facility.
                </p>
            );
        }

        return (
            <p>
                Add everyone that will be going with you. This can be changed any time
                before the permit is issued. Maximum of {maxGroupSize} members at this
                facility.
            </p>
        );
    }

    getMemberBreakdown = () => {
        return this.props.guestTypes.reduce((acc, type) => {
            acc[type.key] = 0;
            return acc;
        }, {});
    };

    handleGuestChange = (value, key) => {
        const group_size = this.props.isBypassRulesValidations
            ? OVERRIDE_MAX_VALUE
            : this.props.maxMemberQuota;

        if (!this.props.isBypassRulesValidations) {
            // if the number is already too big or negative.
            if (value > group_size || value < 0) {
                return;
            }
        }

        const memberBreakdown = this.getMemberBreakdown();

        this.props.groupMembers.forEach((member) => {
            if (member.isAdult) {
                memberBreakdown.adult += 1;
            } else if (member.isChild) {
                memberBreakdown.child += 1;
            }
        });

        // apply desired change here
        memberBreakdown[key] = value;

        // new sum!
        const sum = memberBreakdown.adult + memberBreakdown.child;
        if (sum <= group_size) {
            // good change!
            const newGroupMembers = [];
            Object.keys(memberBreakdown).forEach((type) => {
                for (let i = 0; i < memberBreakdown[type]; i++) {
                    const newMember = { startDate: this.props.entryDate };
                    if (type === 'adult') {
                        newMember.isAdult = true;
                    } else if (type === 'child') {
                        newMember.isChild = true;
                    }
                    newGroupMembers.push(newMember);
                }
            });
            this.props.onChange(newGroupMembers);
        } else {
            // alert the user of a bad change?
        }
    };

    renderChangeDetails = () => {
        const {
            lottery_awarded: lotteryAwarded,
            lottery_accepted: lotteryAccepted,
            status,
        } = get(this.props, 'issuance', '');
        const isLottery =
            (lotteryAwarded || lotteryAccepted) &&
            status !== issuanceStatuses.get(PERMIT_COMPLETE);
        if (!this.props.hasSecureQuotaText) {
            // if quota isn't determined by group size, we don't display this button or text here.
            return null;
        }

        if (this.props.permit.id === ENCHANTMENT_ID && isLottery) {
            return null;
        }

        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        appearance="link"
                        className="change-details-link"
                        screenReaderTextBefore="Redirect to detailed availability to modify the group size of current reservation"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                    >
                        {this.props.modifyDetailsButtonText}
                    </Button>
                </div>
            );
        }
        return NoQuotaMods;
    };

    renderGuestTypes = () => {
        const memberBreakdown = this.getMemberBreakdown();

        this.props.groupMembers.forEach((member) => {
            if (member.isChild) {
                memberBreakdown.child += 1;
            } else {
                memberBreakdown.adult += 1;
            }
        });

        const types = this.props.guestTypes.map((type) => {
            const quotaUsedByOtherGuesTypes = this.props.guestTypes.reduce(
                (accQuotaUsed, guestType) => {
                    if (guestType.key !== type.key) {
                        return accQuotaUsed + memberBreakdown[guestType.key];
                    }
                    return accQuotaUsed;
                },
                0
            );

            const maxQuotaForThisGuestType =
                this.props.maxMemberQuota - quotaUsedByOtherGuesTypes;
            const minQuotaForThisGuestType = Math.max(
                0,
                this.props.minMemberQuota - quotaUsedByOtherGuesTypes
            );

            return (
                <FlexRow className="per-group-member-type" key={type.key}>
                    <FlexCol md={4} alignSelf="center">
                        <label htmlFor={type.key}>
                            {type.type} ({type.description})
                        </label>
                    </FlexCol>
                    <FlexCol md={4}>
                        <NumberField
                            id={type.key}
                            units="guests"
                            min={minQuotaForThisGuestType}
                            max={maxQuotaForThisGuestType}
                            value={memberBreakdown[type.key]}
                            onChange={(value) => this.handleGuestChange(value, type.key)}
                        />
                    </FlexCol>
                </FlexRow>
            );
        });

        return <div className="per-group-member-type-list">{types}</div>;
    };

    renderGroupSize = () => {
        const group_size = this.props.isBypassRulesValidations
            ? OVERRIDE_MAX_VALUE
            : this.props.maxMemberQuota;
        const currentSize = this.props.groupMembers.length;
        const validationStyle = getValidationErrorStyle(
            this.props.validation,
            IS_GROUP_MEMBER_EMPTY,
            'rec-error'
        );
        const groupCheck =
            this.props.validation && this.props.validation[IS_GROUP_MEMBER_EMPTY]
                ? this.props.validation[IS_GROUP_MEMBER_EMPTY]
                : null;
        const validationMessage =
            groupCheck && this.props.validation[IS_GROUP_MEMBER_EMPTY]?.message
                ? this.props.validation[IS_GROUP_MEMBER_EMPTY]?.message
                : groupCheck;
        return (
            <div className="per-total-count-wrapper">
                <span className={`per-total-count ${validationStyle}`}>
                    Total Group Size: <b>{`${currentSize} / ${group_size}`}</b>
                </span>
                {validationStyle && validationMessage}
            </div>
        );
    };

    render() {
        return (
            <div className="per-group-member-form per-section-group">
                <div className="rec-order-detail-subsection-title">Group Size</div>
                {this.renderGroupSize()}
                <FlexRow>
                    <FlexCol md={4}>
                        <VisualOnlyLabel>Type</VisualOnlyLabel>
                    </FlexCol>
                    <FlexCol md={2}>
                        <VisualOnlyLabel isRequired>Quantity</VisualOnlyLabel>
                    </FlexCol>
                </FlexRow>
                {this.renderGuestTypes()}
                {this.renderChangeDetails()}
            </div>
        );
    }
}

export default SimpleGroupMemberCounter;
